/**
 * Чистые (без React) утилиты локали (Фаза 4, И6, §2.9).
 *
 * Вынесены из [`LanguageSwitcher.tsx`](./LanguageSwitcher.tsx) в отдельный модуль,
 * чтобы (а) покрывать их table-driven юнит-тестом без рендера React и (б) не мешать
 * react-refresh/Fast Refresh в `.tsx` (правило `react-refresh/only-export-components`:
 * файл компонента должен экспортировать только компоненты).
 *
 * 0.14.2: `buildLocaleUrl` (навигация на поддомен при смене языка) удалён —
 * переключение языка теперь на лету без перезагрузки (см. LanguageSwitcher.tsx).
 * `apexDomainFromHost` остаётся: он нужен [`ThemeToggle.tsx`](./ThemeToggle.tsx)
 * для cookie `Domain=.<apex>`, общей для языковых поддоменов.
 */
// SVG-флаги (flag-icons, viewBox 0 0 640 480, ratio 4:3) — self-hosted ассеты:
// Vite эмитит хэшированный URL с cache-busting; отдаются nginx'ом как ститика.
// Заменяют emoji: на Windows-десктопе emoji-флаги рендерятся буквами (R7).
// `*.svg`-импорт объявлен через vite/client (vite-env.d.ts) — возвращает string-URL.
import type { Locale } from '../i18n/locale';
import ruFlag from './flags/ru.svg';
import enFlag from './flags/en.svg';
import cnFlag from './flags/cn.svg';

/** Описание одной опции переключателя. Метки — из каталога `lang.*`. */
export interface LangOption {
  code: Locale;
  /** URL self-hosted SVG-флага (4:3). Рендерится <img> в LanguageSwitcher. */
  flag: string;
  /** Ключ каталога языковой метки (`lang.ru` / `lang.en` / `lang.cn`). */
  labelKey: string;
}

/**
 * Три опции переключателя (порядок: ru → en → cn). `as const` фиксирует литералы.
 *
 * Метки — **языковые**, НЕ ISO-коды стран (решение заказчика):
 *  - Рус → apex (ru, по умолчанию) — флаг РФ;
 *  - EN  → `en.`-поддомен — флаг Великобритании (де-факто иконка английского);
 *  - 中文 → `cn.`-поддомен — флаг КНР (метка иероглифами, НЕ код «CN»).
 *
 * Флаги — self-hosted SVG: 0 внешних зависимостей, CSP-friendly, единый рендер
 * на всех ОС (в отличие от emoji-флагов, которые на Windows показываются буквами).
 */
export const LANG_OPTIONS: readonly LangOption[] = [
  { code: 'ru', flag: ruFlag, labelKey: 'lang.ru' },
  { code: 'en', flag: enFlag, labelKey: 'lang.en' },
  { code: 'cn', flag: cnFlag, labelKey: 'lang.cn' },
] as const;

/**
 * Отрезает языковой (`en.`/`cn.`) и/или `www.`-префикс от hostname, возвращая
 * apex-домен (источник для cookie Domain в ThemeToggle). Регистронезависима.
 *
 * Примеры: `en.kikayoo.com` → `kikayoo.com`; `cn.kikayoo.com` → `kikayoo.com`;
 * `www.kikayoo.com` → `kikayoo.com`; `kikayoo.com` → `kikayoo.com`;
 * `localhost` → `localhost`; `en.localhost` → `localhost`.
 */
export function apexDomainFromHost(hostname: string): string {
  const h = hostname.toLowerCase();
  if (h.startsWith('en.')) return h.slice('en.'.length);
  if (h.startsWith('cn.')) return h.slice('cn.'.length);
  if (h.startsWith('www.')) return h.slice('www.'.length);
  return h;
}
